import React, { useEffect } from "react";
import { Grid, styled } from "@mui/material";
import { IconButton } from "../IconButton/IconButton";
import ExpandMoreSharp from "@mui/icons-material/ExpandMoreSharp";
import ExpandLessSharp from "@mui/icons-material/ExpandLessSharp";
import Tooltip from "@mui/material/Tooltip";
import classNames from "classnames";
import { FlightChip, IFlightChip } from "./FlightChip";
import { useDrag } from "react-dnd";

type IFlightGroup = IFlightChip[]; // | IFlight[];
export interface IFlightGroupChip {
  data?: IFlightGroup;
  groupKey?: string;
  onClickGroup?: (groupKey: string) => void;
  onClickChip?: (flightId: string) => void;
  collapseOnClickChip?: boolean;
  buttonAlignRight?: boolean;
  hideGroupCount?: boolean;
  clickGroupToToggle?: boolean;
  groupIsExpanded?: (state: boolean) => void;
}

const OCFlightGroupChip = styled(Grid)(({ theme }) => ({
  zIndex: 1,
  display: "flex",
  flexDirection: "row",
  margin: 5,
  width: "auto",
  "& .chips": {
    position: "relative",
    flexGrow: 1,
    transition: theme.transitions.create(["transform"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shortest,
    }),
    "&.interactive": {
      "&:hover": { transform: "Scale(1.05)", cursor: "pointer" },
      "&:active": { transform: "Scale(.95)" },
    },
  },
  "&.expandRight": { flexDirection: "row-reverse" },
  "&.expanded": {
    transition: theme.transitions.create(["height"], {
      easing: theme.transitions.easing.easeIn,
      duration: theme.transitions.duration.standard,
    }),
    "& .flightchip_container": {
      transform: "translate(0px, 0px)",
      zIndex: 0,
      transition: theme.transitions.create(["transform"], {
        easing: theme.transitions.easing.easeIn,
        duration: theme.transitions.duration.standard,
      }),
    },
  },
  "&.collapsed": {
    transition: theme.transitions.create(["height"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.standard,
    }),
    "& .flightchip_container": {
      position: "absolute",
      transform: "translate(0px, 0px)",
      zIndex: 0,
      transition: theme.transitions.create(["transform"], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.standard,
      }),
      "&.chip_0": { transform: "translate(0px, 0px)", zIndex: 5 },
      "&.chip_1": { transform: "translate(0px, 4px)", zIndex: 4 },
      "&.chip_2": { transform: "translate(0px, 8px)", zIndex: 3 },
      "&.chip_3": { transform: "translate(0px, 12px)", zIndex: 2 },
      "&.chip_4, &.chip_N": {
        transform: "translate(0px, 16px)",
        zIndex: 1,
        "& .flightchip": { boxShadow: "none" },
      },
    },
  },
  "& .toggle_expand_button": {
    height: 24,
    padding: 0,
    "& svg": { marginTop: 4 },
    "& .group_count": {
      position: "absolute",
      fontSize: 10,
      marginTop: -12,
    },
  },
  "& .single": { marginLeft: 32 },
}));

/**
 * Move selected flight to the top of the flights array.
 * @param data -
 * @param flightId -
 */
const bringToFront = (data: IFlightGroup, flightId: string): IFlightGroup => {
  if (data.sort) {
    return data.sort((a: any, b: any) => (a._id === flightId ? -1 : b._id === flightId ? 1 : 0));
  } else {
    return data;
  }
};

// FLIGHT GROUP CHIP
export const FlightGroupChip = (props: IFlightGroupChip): JSX.Element => {
  const [expand, setExpand] = React.useState<boolean>(false);
  const { data, onClickChip, collapseOnClickChip, buttonAlignRight, hideGroupCount, groupIsExpanded, onClickGroup } =
    props;
  const [selectedChip, setSelectedChip] = React.useState<string>(null);
  const groupId = "flightgroup";
  const [sortedGroupChips, setSortedGroupChips] = React.useState<IFlightGroup>(data);

  const dragType = data?.[0]?.aircraft ? "FLIGHTS" : "UNASSIGNED_FLIGHTS";
  const [collected, drag, dragPreview] = useDrag(
    {
      type: dragType,
      item: { flights: data, type: dragType },
    },
    [dragType, data]
  );

  // Update parent on status of group's expanded state.
  useEffect(() => {
    if (groupIsExpanded) groupIsExpanded(expand);
  }, [groupIsExpanded, expand]);

  //if the flight group only contains one, allow onClick to assign flight
  useEffect(() => {
    if (sortedGroupChips.length === 1) setExpand(true);
  }, [sortedGroupChips]);

  useEffect(() => {
    const selectedFlight = selectedChip ? selectedChip : data[0]?._id;
    const sortedFlights = bringToFront(data, selectedFlight);
    setSelectedChip(selectedFlight);
    setSortedGroupChips(sortedFlights);
  }, [data, selectedChip]);

  /**
   * fire off function passed in from parent when user clicks on a flight group.
   */
  const handleOnClickGroup = (event: any): void => {
    const { onClickGroup, groupKey, clickGroupToToggle } = props;
    if (sortedGroupChips.length > 1 && clickGroupToToggle) setExpand(!expand);
    // If handler is passed in from props, fire it off
    if (onClickGroup && typeof onClickGroup === "function" && !!groupKey && !expand) {
      onClickGroup(groupKey);
    }
  };

  // Function to resort group and send select chipId to parent.
  // If collapseOnClickChip is true, will resort the group so that the selected chip appears at the top.
  const handleOnClickChip = (flightId: string): void => {
    if (!onClickChip || typeof onClickChip !== "function") {
      return;
    }
    if (collapseOnClickChip) {
      setExpand(false);
      setSelectedChip(flightId);
    }
    onClickChip(flightId);
  };

  // This function is used only when the group count button is displayed.
  const handleToggleExpand = (event: any): void => {
    setExpand(!expand);
    if (props.groupIsExpanded) props.groupIsExpanded(!expand); // update parent.
  };

  if (!!sortedGroupChips && sortedGroupChips.length > 0) {
    // Remove max expanded height for now.
    const maxCollapsedCount = sortedGroupChips.length <= 5 ? sortedGroupChips.length : 5;
    const groupHeightCollapsed: number = 28 + maxCollapsedCount * 3;

    const maxExpandedCount = sortedGroupChips.length;
    const groupHeightExpanded: number = 28 * maxExpandedCount + 2;

    return (
      <OCFlightGroupChip
        container
        ref={drag && !expand ? drag : null}
        key={groupId}
        id={groupId}
        className={classNames(expand ? "expanded" : "collapsed", buttonAlignRight && "expandRight")}
        style={{
          height: `${expand ? groupHeightExpanded : groupHeightCollapsed}px`,
          width: hideGroupCount || sortedGroupChips.length === 1 ? 124 : 150,
        }}
      >
        {sortedGroupChips.length > 1 && !hideGroupCount && (
          <IconButton id="toggle_expand_btn" className="toggle_expand_button" onClick={handleToggleExpand} size="large">
            {!expand ? <ExpandMoreSharp /> : <ExpandLessSharp />}
            <div className="group_count">{sortedGroupChips.length > 0 && sortedGroupChips.length}</div>
          </IconButton>
        )}

        <Tooltip title={`Group Event Count: ${sortedGroupChips.length}`} enterDelay={1000} placement="top">
          <Grid
            item
            className={classNames("chips", !!onClickGroup && !expand && "interactive")}
            onClick={handleOnClickGroup}
          >
            {sortedGroupChips.map((chipData: any, index: number) => {
              return (
                <div
                  key={`${chipData._id}-flightChip`}
                  className={classNames("flightchip_container", index < 5 ? `chip_${index}` : "chip_N")}
                >
                  <FlightChip
                    data={chipData}
                    onClickChip={expand && onClickChip ? handleOnClickChip : null}
                    disableContext={!expand}
                    disableDrag={!expand}
                  />
                </div>
              );
            })}
          </Grid>
        </Tooltip>
      </OCFlightGroupChip>
    );
  }
  return null;
};
